import { QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { queryClient } from './api/query-client';
import { AuthProvider } from './auth/AuthProvider';
import { FederatedCallbackPage } from './auth/FederatedCallbackPage';
import { LoginPage } from './auth/LoginPage';
import { ProtectedRoute } from './auth/ProtectedRoute';
import { AppLayout } from './components/shared/app-layout';
import { ErrorBoundary } from './components/shared/error-boundary';
import { Loading } from './components/shared/loading';
import { ThemeProvider } from './components/shared/theme-provider';
import { Toaster } from './components/ui/toaster';

const ReactQueryDevtools = lazy(() =>
    import('@tanstack/react-query-devtools').then((m) => ({ default: m.ReactQueryDevtools })),
);

const DashboardPage = lazy(() => import('./pages/dashboard').then((m) => ({ default: m.DashboardPage })));
const NotFoundPage = lazy(() => import('./pages/not-found').then((m) => ({ default: m.NotFoundPage })));
const BrandsListPage = lazy(() =>
    import('./features/brands/brands-list-page').then((m) => ({ default: m.BrandsListPage })),
);
const BrandCreatePage = lazy(() =>
    import('./features/brands/brand-create-page').then((m) => ({ default: m.BrandCreatePage })),
);
const BrandDetailPage = lazy(() =>
    import('./features/brands/brand-detail-page').then((m) => ({ default: m.BrandDetailPage })),
);
const BrandContextPage = lazy(() =>
    import('./features/brands/brand-context-page').then((m) => ({ default: m.BrandContextPage })),
);
const VersionsListPage = lazy(() =>
    import('./features/versions/versions-list-page').then((m) => ({ default: m.VersionsListPage })),
);
const TeamManagementPage = lazy(() =>
    import('./features/team/team-management-page').then((m) => ({ default: m.TeamManagementPage })),
);
const PaymentsPage = lazy(() => import('./features/payments/payments-page').then((m) => ({ default: m.PaymentsPage })));
const AiActionsPage = lazy(() =>
    import('./features/ai-actions/ai-actions-page').then((m) => ({ default: m.AiActionsPage })),
);
const AiUsagePage = lazy(() => import('./features/ai-usage/ai-usage-page').then((m) => ({ default: m.AiUsagePage })));
const RateCardsPage = lazy(() =>
    import('./features/rate-cards/rate-cards-page').then((m) => ({ default: m.RateCardsPage })),
);
const PaymentPlansPage = lazy(() =>
    import('./features/payment-plans/payment-plans-page').then((m) => ({ default: m.PaymentPlansPage })),
);
const CollectionsPage = lazy(() =>
    import('./features/assets/collections-page').then((m) => ({ default: m.CollectionsPage })),
);
const ColorsPage = lazy(() => import('./features/assets/colors-page').then((m) => ({ default: m.ColorsPage })));
const FontsPage = lazy(() => import('./features/assets/fonts-page').then((m) => ({ default: m.FontsPage })));
const StickersPage = lazy(() => import('./features/assets/stickers-page').then((m) => ({ default: m.StickersPage })));
const OrganizationsListPage = lazy(() =>
    import('./features/organizations/organizations-list-page').then((m) => ({ default: m.OrganizationsListPage })),
);
const OrganizationDetailPage = lazy(() =>
    import('./features/organizations/organization-detail-page').then((m) => ({ default: m.OrganizationDetailPage })),
);

const DomainsPage = lazy(() => import('./features/domains/domains-page').then((m) => ({ default: m.DomainsPage })));
const BrandDomainsPage = lazy(() =>
    import('./features/domains/brand-domains-page').then((m) => ({ default: m.BrandDomainsPage })),
);

function App() {
    return (
        <ErrorBoundary>
            <QueryClientProvider client={queryClient}>
                <ThemeProvider defaultTheme="system" storageKey="slate-admin-theme">
                    <BrowserRouter>
                        <AuthProvider>
                            <Suspense fallback={<Loading />}>
                                <Routes>
                                    <Route path="/login" element={<LoginPage />} />
                                    <Route path="/auth/federated/login" element={<FederatedCallbackPage />} />
                                    <Route
                                        path="/"
                                        element={
                                            <ProtectedRoute>
                                                <AppLayout>
                                                    <DashboardPage />
                                                </AppLayout>
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/brands"
                                        element={
                                            <ProtectedRoute>
                                                <AppLayout>
                                                    <BrandsListPage />
                                                </AppLayout>
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/brands/new"
                                        element={
                                            <ProtectedRoute>
                                                <AppLayout>
                                                    <BrandCreatePage />
                                                </AppLayout>
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/brands/:identifier"
                                        element={
                                            <ProtectedRoute>
                                                <AppLayout>
                                                    <BrandDetailPage />
                                                </AppLayout>
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/brands/:identifier/versions"
                                        element={
                                            <ProtectedRoute>
                                                <AppLayout>
                                                    <VersionsListPage />
                                                </AppLayout>
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/brands/:identifier/team"
                                        element={
                                            <ProtectedRoute>
                                                <AppLayout>
                                                    <TeamManagementPage />
                                                </AppLayout>
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/brands/:identifier/context"
                                        element={
                                            <ProtectedRoute>
                                                <AppLayout>
                                                    <BrandContextPage />
                                                </AppLayout>
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/brands/:identifier/payments"
                                        element={
                                            <ProtectedRoute>
                                                <AppLayout>
                                                    <PaymentsPage />
                                                </AppLayout>
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/brands/:identifier/ai-actions"
                                        element={
                                            <ProtectedRoute>
                                                <AppLayout>
                                                    <AiActionsPage />
                                                </AppLayout>
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/brands/:identifier/stickers"
                                        element={
                                            <ProtectedRoute>
                                                <AppLayout>
                                                    <StickersPage />
                                                </AppLayout>
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/brands/:identifier/colors"
                                        element={
                                            <ProtectedRoute>
                                                <AppLayout>
                                                    <ColorsPage />
                                                </AppLayout>
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/brands/:identifier/fonts"
                                        element={
                                            <ProtectedRoute>
                                                <AppLayout>
                                                    <FontsPage />
                                                </AppLayout>
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/brands/:identifier/collections"
                                        element={
                                            <ProtectedRoute>
                                                <AppLayout>
                                                    <CollectionsPage />
                                                </AppLayout>
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/ai-usage"
                                        element={
                                            <ProtectedRoute>
                                                <AppLayout>
                                                    <AiUsagePage />
                                                </AppLayout>
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/rate-cards"
                                        element={
                                            <ProtectedRoute>
                                                <AppLayout>
                                                    <RateCardsPage />
                                                </AppLayout>
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/payment-plans"
                                        element={
                                            <ProtectedRoute>
                                                <AppLayout>
                                                    <PaymentPlansPage />
                                                </AppLayout>
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/organizations"
                                        element={
                                            <ProtectedRoute>
                                                <AppLayout>
                                                    <OrganizationsListPage />
                                                </AppLayout>
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/organizations/:organizationId"
                                        element={
                                            <ProtectedRoute>
                                                <AppLayout>
                                                    <OrganizationDetailPage />
                                                </AppLayout>
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/brands/:identifier/domains"
                                        element={
                                            <ProtectedRoute>
                                                <AppLayout>
                                                    <BrandDomainsPage />
                                                </AppLayout>
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/domains"
                                        element={
                                            <ProtectedRoute>
                                                <AppLayout>
                                                    <DomainsPage />
                                                </AppLayout>
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route path="*" element={<NotFoundPage />} />
                                </Routes>
                            </Suspense>
                            <Toaster />
                        </AuthProvider>
                    </BrowserRouter>
                </ThemeProvider>
                {import.meta.env.DEV && (
                    <Suspense fallback={null}>
                        <ReactQueryDevtools initialIsOpen={false} />
                    </Suspense>
                )}
            </QueryClientProvider>
        </ErrorBoundary>
    );
}

export default App;
